// The Deck Stats panel in the deck builder: the cost curve, what the deck is made of,
// and a guess at its archetype. The numbers and the guess come from src/shared/archetypes.ts.
import { RARITIES, CARD_TYPES, AFFILIATIONS, titleCase } from '../shared/cards.ts';
import { MAIN_DECK_SIZE, type Deck } from '../shared/deck.ts';
import { COST_BANDS, MIN_CARDS_TO_GUESS, bandOf, guessArchetypes, profileDeck, type ArchetypeFit } from '../shared/archetypes.ts';
import { CARD_DB } from './data.ts';
import { el, button, modal } from './ui.ts';

// ---------------- one tooltip shared by every bar ----------------
let tip: HTMLElement | null = null;
function showTip(target: HTMLElement, value: string, label: string) {
  tip ??= el('div', { class: 'viz-tip', role: 'tooltip' });
  tip.replaceChildren(el('strong', {}, value), el('span', {}, label));
  document.body.append(tip);
  const r = target.getBoundingClientRect();
  const w = tip.offsetWidth;
  tip.style.left = `${Math.max(8, Math.min(window.innerWidth - w - 8, r.left + r.width / 2 - w / 2))}px`;
  tip.style.top = `${Math.max(8, r.top - tip.offsetHeight - 8)}px`;
}
function hideTip() {
  tip?.remove();
}
/** Makes a mark answer the mouse and the keyboard with the same readout. */
function withTip<T extends HTMLElement>(node: T, value: string, label: string): T {
  node.tabIndex = 0;
  node.setAttribute('aria-label', `${label}: ${value}`);
  node.addEventListener('pointerenter', () => showTip(node, value, label));
  node.addEventListener('pointerleave', hideTip);
  node.addEventListener('focus', () => showTip(node, value, label));
  node.addEventListener('blur', hideTip);
  return node;
}

const cards = (n: number) => `${n} card${n === 1 ? '' : 's'}`;

// ---------------- the pieces ----------------

function tile(label: string, value: string, note = '') {
  return el('div', { class: 'stat-tile' }, el('span', { class: 'stat-label' }, label), el('span', { class: 'stat-value' }, value), note ? el('span', { class: 'stat-note' }, note) : null);
}

/** Cost curve: one column per Gerzite cost, as tall as the number of cards at that cost, over the four cost bands. */
function costCurve(curve: Map<number, number>) {
  const costs = [...curve.keys()];
  const last = Math.max(20, ...costs);
  const first = Math.min(1, ...costs);
  const peak = Math.max(1, ...curve.values());
  const columns = [];
  for (let cost = first; cost <= last; cost++) {
    const n = curve.get(cost) ?? 0;
    columns.push(
      withTip(
        el(
          'div',
          { class: 'curve-slot' },
          // only the tallest column carries its number; the rest are a hover (or the table) away
          el('span', { class: 'curve-value' }, n === peak && n > 0 ? String(n) : ''),
          el('span', { class: 'curve-bar', style: { height: `${(n / peak) * 100}%` } }),
          el('span', { class: 'curve-tick' }, String(cost)),
        ),
        cards(n),
        `Cost ${cost} (${bandOf(cost).name.toLowerCase()})`,
      ),
    );
  }
  // under the axis: the four bands, each as wide as the costs it covers, with its share of the cards
  const bands = COST_BANDS.map((band) => {
    const from = Math.max(first, band.from);
    const to = Math.min(last, band.to);
    if (to < from) return null;
    let n = 0;
    for (const [cost, count] of curve) if (cost >= band.from && cost <= band.to) n += count;
    const range = band.to === Infinity ? `${band.from} and up` : band.from <= 0 ? `${band.to} or less` : `${band.from} to ${band.to}`;
    return withTip(
      el('div', { class: 'curve-band', style: { gridColumn: `span ${to - from + 1}` } }, el('span', { class: 'curve-band-name' }, band.name), el('span', { class: 'curve-band-count' }, String(n))),
      cards(n),
      `${band.name}: cost ${range}`,
    );
  });
  const grid = { gridTemplateColumns: `repeat(${columns.length}, 1fr)` };
  return el(
    'div',
    { class: 'curve', role: 'img', 'aria-label': 'Cost curve: cards at each Gerzite cost' },
    el('div', { class: 'curve-plot', style: grid }, columns),
    el('div', { class: 'curve-bands', style: grid }, bands),
  );
}

/** Horizontal bars: a label, a bar, and its number at the tip. */
function bars(rows: { label: string; n: number; note?: string; lead?: boolean; unit?: string }[], max?: number, kind = '') {
  const peak = max ?? Math.max(1, ...rows.map((r) => r.n));
  return el(
    'div',
    { class: `hbars ${kind}` },
    rows.map((r) =>
      withTip(
        el(
          'div',
          { class: `hbar ${r.lead ? 'is-lead' : ''}` },
          el('span', { class: 'hbar-label' }, r.label),
          el('span', { class: 'hbar-track' }, el('span', { class: 'hbar-fill', style: { width: `${Math.max(0, (r.n / peak) * 100)}%` } })),
          el('span', { class: 'hbar-value' }, String(r.n)),
        ),
        r.unit ? `${r.n} ${r.unit}` : cards(r.n),
        r.note ?? r.label,
      ),
    ),
  );
}

function countRows(order: readonly string[], counts: Map<string, number>) {
  const known = order.filter((k) => (counts.get(k) ?? 0) > 0).map((k) => ({ label: titleCase(k), n: counts.get(k)! }));
  const extra = [...counts.keys()].filter((k) => !order.includes(k)).map((k) => ({ label: k === 'unassigned' ? (order.includes('neutral') ? 'No affiliation' : 'Not filled in') : titleCase(k), n: counts.get(k)! }));
  return [...known, ...extra];
}

/** How many archetypes the panel lists (the closest ones). */
const SHOWN_ARCHETYPES = 10;

function archetypePanel(fits: ArchetypeFit[], total: number, costKnown: boolean) {
  if (!fits.length) {
    return el('p', { class: 'muted' }, `Add at least ${MIN_CARDS_TO_GUESS} cards and the panel will guess what kind of deck this is. (${total} so far.)`);
  }
  const [best, second] = fits;
  const clear = best.fit >= 40;
  const close = second && best.fit - second.fit <= 6;
  const reasonList = (f: ArchetypeFit) => (f.reasons.length ? el('ul', { class: 'guess-why' }, f.reasons.map((r) => el('li', {}, r[0].toUpperCase() + r.slice(1) + '.'))) : null);
  const top = fits.slice(0, SHOWN_ARCHETYPES);
  return el(
    'div',
    { class: 'stack-tight' },
    el(
      'div',
      { class: 'guess' },
      el('span', { class: 'guess-label' }, clear ? 'Best guess' : 'No clear archetype yet. Closest'),
      el('span', { class: 'guess-name' }, close ? `${best.name} or ${second.name}` : best.name),
      el(
        'details',
        { class: 'arch-more' },
        el('summary', {}, 'What it means and why'),
        el('p', { class: 'guess-plan' }, best.plan),
        reasonList(best),
        close && second.reasons.length ? el('p', { class: 'muted small' }, `${second.name} is nearly as close: ${second.reasons[0]}.`) : null,
      ),
    ),
    costKnown ? null : el('p', { class: 'muted small' }, 'Most cards in this deck have no cost yet, so the guess goes by card types, abilities and leanings only, and every score is lower than it will be. It sharpens once the costs are in cards.csv.'),
    el('p', { class: 'muted small' }, `The ${top.length} archetypes this deck is closest to. Click one to read what it means.`),
    el(
      'div',
      { class: 'hbars fit-bars' },
      top.map((f, i) =>
        el(
          'details',
          { class: 'arch-item' },
          el(
            'summary',
            { class: `hbar ${i === 0 ? 'is-lead' : ''}` },
            el('span', { class: 'hbar-label' }, f.name),
            el('span', { class: 'hbar-track' }, el('span', { class: 'hbar-fill', style: { width: `${Math.max(0, f.fit)}%` } })),
            el('span', { class: 'hbar-value' }, String(f.fit)),
          ),
          el('div', { class: 'arch-body' }, el('p', { class: 'guess-plan' }, f.plan), reasonList(f)),
        ),
      ),
    ),
  );
}

/** A plain table of every number in the panel, for reading without hovering. */
function tableView(sections: { title: string; rows: { label: string; n: number }[] }[]) {
  return el(
    'details',
    { class: 'viz-table' },
    el('summary', {}, 'Show these numbers as a table'),
    el(
      'div',
      { class: 'viz-table-grid' },
      sections.map((s) =>
        el('table', {}, el('caption', {}, s.title), el('tbody', {}, s.rows.map((r) => el('tr', {}, el('th', { scope: 'row' }, r.label), el('td', {}, String(r.n)))))),
      ),
    ),
  );
}

export function showDeckStats(deck: Deck, name: string) {
  const p = profileDeck(deck, CARD_DB);
  const fits = guessArchetypes(p);
  const types = countRows(CARD_TYPES, p.byType);
  const rarities = countRows(RARITIES, p.byRarity);
  const affiliations = countRows(AFFILIATIONS, p.byAffiliation).sort((a, b) => b.n - a.n);
  const curveRows = [...p.curve.entries()].sort((a, b) => a[0] - b[0]).map(([cost, n]) => ({ label: `Cost ${cost}`, n }));
  const section = (title: string, ...body: (Node | null)[]) => el('section', { class: 'stats-section' }, el('h3', {}, title), ...body);
  let close = () => {};
  const content = el(
    'div',
    { class: 'stack deck-stats' },
    el('div', { class: 'deck-view-head' }, el('h2', {}, `${name} · deck stats`)),
    p.total === 0
      ? el('p', { class: 'empty-note' }, 'There are no cards in the deck yet.')
      : el(
          'div',
          { class: 'stats-body' },
          el(
            'div',
            { class: 'stat-row' },
            tile('Cards', `${p.total}/${MAIN_DECK_SIZE}`),
            tile('Average cost', p.costed ? p.avgCost.toFixed(1) : '–', 'Gerzite'),
            tile('Different cards', String(p.distinct), `${p.avgCopies.toFixed(1)} copies of each`),
            tile('Units', String((p.byType.get('troop') ?? 0) + (p.byType.get('building') ?? 0)), 'Troops and Buildings'),
          ),
          p.unfinished ? el('p', { class: 'muted small' }, `${cards(p.unfinished)} in this deck ${p.unfinished === 1 ? 'has' : 'have'} no type in cards.csv yet, so ${p.unfinished === 1 ? 'it counts' : 'they count'} for less here.`) : null,
          el(
            'div',
            { class: 'stats-grid' },
            el('div', { class: 'stats-col' }, section('Cost curve', p.costed ? costCurve(p.curve) : el('p', { class: 'muted' }, 'None of these cards has a cost yet.')), section('Deck archetype', archetypePanel(fits, p.total, p.costKnown))),
            el('div', { class: 'stats-col' }, section('Card types', bars(types)), section('Rarity', bars(rarities)), section('Affiliation', bars(affiliations))),
          ),
          tableView([
            { title: 'Cost curve', rows: curveRows },
            { title: 'Card types', rows: types },
            { title: 'Rarity', rows: rarities },
            { title: 'Affiliation', rows: affiliations },
            { title: 'Archetype match (out of 100)', rows: fits.slice(0, SHOWN_ARCHETYPES).map((f) => ({ label: f.name, n: f.fit })) },
          ]),
        ),
    el('div', { class: 'row-end' }, button('Close', () => close(), 'btn-primary')),
  );
  close = modal(content, { wide: true, onClose: hideTip });
}
